// PHA-510 admin tab: list + filter + manually trigger EHR sync runs.
// Mirrors AdminEligibilityPage shape; differences are scoped to:
//  * trigger panel above the filters (per-patient + per-org)
//  * status filter via Radix <Select>
//  * detail view via <Dialog> (jsonb payloads are too big to inline-expand)
//  * continuous 10s polling while any row is `running`
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  Text,
  Button,
  Card,
  Table,
  TextField,
  Badge,
  Select,
  Dialog,
  Code,
} from '@radix-ui/themes';
import { ReloadIcon, ExternalLinkIcon } from '@radix-ui/react-icons';
import AdminLayout from './AdminLayout';
import { adminApi } from '../../services/api';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useOrganization } from '../../contexts/OrganizationContext';

interface EhrSyncRunRecord {
  id: number;
  patient_id: string;
  first_name: string | null;
  last_name: string | null;
  org_id: string;
  started_at: string | null;
  completed_at: string | null;
  status: string;
  error_detail: string | null;
  change_summary: ChangeSummary | null;
  applicability_summary: ApplicabilitySummary | null;
  snapshot_attributed_ts: string | null;
  prior_snapshot_attributed_ts: string | null;
  draft_care_plan_id: string | null;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  acknowledgement_dwell_seconds: number | null;
  triggered_by: string | null;
}

interface ChangeBucketItem {
  icd10?: string | null;
  label?: string;
  applicability?: string;
}

interface ChangeSummary {
  problems_added?: ChangeBucketItem[];
  problems_resolved?: ChangeBucketItem[];
  meds_added?: ChangeBucketItem[];
  meds_changed?: ChangeBucketItem[];
}

interface ApplicabilitySummary {
  applicable_count?: number;
  non_applicable_count?: number;
}


const isoOffset = (days: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const PAGE_SIZE = 50;

// "All" sentinel chosen to match Radix Select's no-value semantics — it
// rejects an empty-string value so we use a distinct token instead.
const STATUS_ALL = '__all__';
const STATUS_OPTIONS = [
  { value: STATUS_ALL, label: 'All statuses' },
  { value: 'running', label: 'Running' },
  { value: 'changes_found', label: 'Changes found' },
  { value: 'no_changes_applicable', label: 'No changes applicable' },
  { value: 'no_changes', label: 'No changes' },
  { value: 'error', label: 'Error' },
];

const formatName = (r: EhrSyncRunRecord): string => {
  const parts = [r.first_name, r.last_name].filter(Boolean) as string[];
  return parts.length > 0 ? parts.join(' ') : '—';
};

const formatDuration = (started: string | null, completed: string | null): string => {
  if (!started) return '—';
  const start = new Date(started).getTime();
  const end = completed ? new Date(completed).getTime() : Date.now();
  const sec = Math.max(0, Math.floor((end - start) / 1000));
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  return `${min}m ${sec % 60}s`;
};

const STATUS_BADGE_COLOR: Record<string, 'blue' | 'orange' | 'gray' | 'red' | 'green'> = {
  running: 'blue',
  changes_found: 'orange',
  no_changes_applicable: 'gray',
  no_changes: 'green',
  error: 'red',
};

const bucketSummary = (cs: ChangeSummary | null): string => {
  if (!cs) return '—';
  const parts: string[] = [];
  const push = (n: number, singular: string, plural: string) => {
    if (n > 0) parts.push(`${n} ${n === 1 ? singular : plural}`);
  };
  push((cs.problems_added || []).length, 'new problem', 'new problems');
  push((cs.problems_resolved || []).length, 'resolved', 'resolved');
  push((cs.meds_added || []).length, 'new med', 'new meds');
  push((cs.meds_changed || []).length, 'med change', 'med changes');
  return parts.length > 0 ? parts.join(', ') : '—';
};

const AdminEhrSyncPage: React.FC = () => {
  const navigate = useNavigate();

  // PHA-527: org-state lives in OrganizationContext now. We keep the local
  // names so the rest of this file (and its dep arrays) stays untouched.
  const { orgId: ctxOrgId, loading: orgLoading } = useOrganization();
  const sessionOrgId: string | null = ctxOrgId || null;
  const sessionLoaded = !orgLoading;

  const [records, setRecords] = useState<EhrSyncRunRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [statusFilter, setStatusFilter] = useState<string>(STATUS_ALL);
  const [startDate, setStartDate] = useState(isoOffset(-7));
  const [endDate, setEndDate] = useState(isoOffset(1));
  const [page, setPage] = useState(1);

  // Trigger panel state.
  const [triggerMrn, setTriggerMrn] = useState('');
  const [triggerBusy, setTriggerBusy] = useState(false);
  const [triggerMessage, setTriggerMessage] = useState<{ kind: 'info' | 'error'; text: string } | null>(null);

  // Org-trigger confirmation dialog.
  const [orgConfirmOpen, setOrgConfirmOpen] = useState(false);
  const [orgTriggerBusy, setOrgTriggerBusy] = useState(false);
  // PHA-579: SmartHub AI ad-hoc trigger
  const [aiBusy, setAiBusy] = useState(false);
  const [aiMessage, setAiMessage] = useState<{ kind: 'info' | 'error'; text: string } | null>(null);

  // Row-detail dialog.
  const [detailRow, setDetailRow] = useState<EhrSyncRunRecord | null>(null);

  const loadRuns = useCallback(async () => {
    if (!sessionOrgId || sessionOrgId === 'ALL') {
      setRecords([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await adminApi.listEhrSyncRuns({
        organization_id: sessionOrgId,
        statuses: statusFilter === STATUS_ALL ? undefined : [statusFilter],
        started_after: startDate || undefined,
        started_before: endDate || undefined,
        search: debouncedSearch.trim() || undefined,
        page,
        page_size: PAGE_SIZE,
      });
      setRecords(Array.isArray(data?.records) ? (data.records as EhrSyncRunRecord[]) : []);
      setTotal(typeof data?.total === 'number' ? data.total : 0);
    } catch (e) {
      const err = e as Error;
      // eslint-disable-next-line no-console
      console.error('Failed to load ehr_sync runs:', err);
      setError(err.message || 'Failed to load EHR sync runs');
      setRecords([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [sessionOrgId, statusFilter, startDate, endDate, debouncedSearch, page]);

  useEffect(() => {
    if (sessionLoaded) loadRuns();
  }, [sessionLoaded, loadRuns]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, startDate, endDate]);

  // PHA-510 continuous poll: while ANY row is in `running` state, refetch
  // every 10s so the admin sees status transitions without manually
  // refreshing. Stops as soon as no rows are running OR the component
  // unmounts. Uses a ref to capture the latest loadRuns without retriggering
  // the effect on every callback identity change.
  const loadRunsRef = useRef(loadRuns);
  useEffect(() => {
    loadRunsRef.current = loadRuns;
  }, [loadRuns]);

  useEffect(() => {
    const hasRunning = records.some((r) => r.status === 'running');
    if (!hasRunning) return;
    const id = window.setInterval(() => {
      loadRunsRef.current();
    }, 10_000);
    return () => window.clearInterval(id);
  }, [records]);

  const handleTrigger = async () => {
    const mrn = triggerMrn.trim();
    if (!mrn) {
      setTriggerMessage({ kind: 'error', text: 'Enter an MRN to run sync for.' });
      return;
    }
    if (!sessionOrgId || sessionOrgId === 'ALL') {
      setTriggerMessage({ kind: 'error', text: 'Select an organization first.' });
      return;
    }
    setTriggerBusy(true);
    setTriggerMessage(null);
    try {
      await adminApi.triggerEhrSync({
        organization_id: sessionOrgId,
        patient_id: mrn,
      });
      setTriggerMessage({ kind: 'info', text: `Sync started for ${mrn}. Refreshing in a few seconds…` });
      setTriggerMrn('');
      // Single quick refetch to surface the new `running` row; the polling
      // effect above takes over from there and stops when status flips.
      window.setTimeout(() => {
        loadRunsRef.current();
      }, 2_000);
    } catch (e) {
      const err = e as Error & { status?: number };
      // eslint-disable-next-line no-console
      console.error('triggerEhrSync failed', err);
      setTriggerMessage({
        kind: 'error',
        text: err?.status === 409 ? `${err.message}` : err.message || 'Failed to trigger sync',
      });
    } finally {
      setTriggerBusy(false);
    }
  };

  const handleTriggerOrg = async () => {
    if (!sessionOrgId || sessionOrgId === 'ALL') {
      setTriggerMessage({ kind: 'error', text: 'Select an organization first.' });
      setOrgConfirmOpen(false);
      return;
    }
    setOrgTriggerBusy(true);
    setTriggerMessage(null);
    try {
      const result = await adminApi.triggerEhrSyncForOrg({
        organization_id: sessionOrgId,
      });
      setTriggerMessage({
        kind: 'info',
        text: `Org sync started for ${result?.organization_name || sessionOrgId}: ${result?.eligible_count ?? 0} FHIR patients (capped at ${result?.max_patients ?? 50}).`,
      });
      window.setTimeout(() => {
        loadRunsRef.current();
      }, 2_000);
    } catch (e) {
      const err = e as Error;
      // eslint-disable-next-line no-console
      console.error('triggerEhrSyncForOrg failed', err);
      setTriggerMessage({ kind: 'error', text: err.message || 'Failed to trigger org sync' });
    } finally {
      setOrgTriggerBusy(false);
      setOrgConfirmOpen(false);
    }
  };

  const handleTriggerSmartHubAi = async () => {
    if (!sessionOrgId || sessionOrgId === 'ALL') {
      setAiMessage({ kind: 'error', text: 'Select a specific organization first.' });
      return;
    }
    setAiBusy(true);
    setAiMessage(null);
    try {
      const result = await adminApi.triggerSmartHubAiOrg({ organization_id: sessionOrgId });
      if (result.status === 'skipped_org_flag') {
        setAiMessage({
          kind: 'error',
          text: 'SmartHub AI is not enabled for this org. Set organizations.smart_hub_ai_enabled = TRUE first.',
        });
      } else {
        setAiMessage({
          kind: 'info',
          text: `Computed ${result.patients_processed ?? 0} of ${result.patients_attempted ?? 0} patients`
            + (result.failures ? `, ${result.failures} failures` : '')
            + (result.pipeline_run_id ? ` (run ${result.pipeline_run_id.slice(0, 8)}…)` : ''),
        });
      }
    } catch (e) {
      const err = e as Error;
      // eslint-disable-next-line no-console
      console.error('triggerSmartHubAiOrg failed', err);
      setAiMessage({ kind: 'error', text: err.message || 'Failed to trigger SmartHub AI' });
    } finally {
      setAiBusy(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <AdminLayout
      title="EHR Sync"
      subtitle="Daily EHR FHIR refresh — view history and run on demand."
    >
      <Flex direction="column" gap="3">
        {/* Trigger panel */}
        <Card>
          <Flex direction="column" gap="3">
            <Text size="2" weight="medium">
              Run EHR sync now
            </Text>
            <Flex gap="2" wrap="wrap" align="end">
              <Box style={{ minWidth: 280, flex: '1 1 280px' }}>
                <Text size="1" color="gray" mb="1" as="div">
                  Patient MRN
                </Text>
                <TextField.Root>
                  <TextField.Input
                    placeholder="Enter FHIR patient MRN"
                    value={triggerMrn}
                    onChange={(e) => setTriggerMrn(e.currentTarget.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleTrigger();
                    }}
                    disabled={triggerBusy}
                  />
                </TextField.Root>
              </Box>
              <Button
                onClick={handleTrigger}
                disabled={triggerBusy || !sessionOrgId || sessionOrgId === 'ALL'}
              >
                {triggerBusy ? 'Starting…' : 'Run for patient'}
              </Button>
              <Button
                variant="soft"
                color="amber"
                onClick={() => setOrgConfirmOpen(true)}
                disabled={orgTriggerBusy || !sessionOrgId || sessionOrgId === 'ALL'}
              >
                Run for full org now
              </Button>
            </Flex>
            {triggerMessage && (
              <Box
                style={{
                  padding: 10,
                  background: triggerMessage.kind === 'error' ? 'var(--red-3)' : 'var(--blue-3)',
                  borderRadius: 4,
                }}
              >
                <Text size="2" color={triggerMessage.kind === 'error' ? 'red' : 'blue'}>
                  {triggerMessage.text}
                </Text>
              </Box>
            )}
          </Flex>
        </Card>

        {/* PHA-579: SmartHub AI ad-hoc compute */}
        <Card>
          <Flex direction="column" gap="3">
            <Flex direction="column" gap="1">
              <Text size="2" weight="medium">
                Run SmartHub AI compute now
              </Text>
              <Text size="1" color="gray">
                Fires the same nightly pipeline for the selected org on demand.
                Capped by SMARTHUB_AI_MAX_PATIENTS. Requires the org to have
                SmartHub AI enabled.
              </Text>
            </Flex>
            <Flex gap="2" wrap="wrap" align="end">
              <Button
                variant="soft"
                color="purple"
                onClick={handleTriggerSmartHubAi}
                disabled={aiBusy || !sessionOrgId || sessionOrgId === 'ALL'}
              >
                {aiBusy ? 'Computing… (may take ~1 min)' : 'Run SmartHub AI for full org now'}
              </Button>
            </Flex>
            {aiMessage && (
              <Box
                style={{
                  padding: 10,
                  background: aiMessage.kind === 'error' ? 'var(--red-3)' : 'var(--blue-3)',
                  borderRadius: 4,
                }}
              >
                <Text size="2" color={aiMessage.kind === 'error' ? 'red' : 'blue'}>
                  {aiMessage.text}
                </Text>
              </Box>
            )}
          </Flex>
        </Card>

        {/* Filter + table */}
        <Card>
          <Flex direction="column" gap="3">
            <Flex gap="3" wrap="wrap" align="end">
              <Box style={{ minWidth: 240, flex: '1 1 240px' }}>
                <Text size="1" color="gray" mb="1" as="div">
                  Search MRN or name
                </Text>
                <TextField.Root>
                  <TextField.Input
                    placeholder="MRN / first name / last name"
                    value={search}
                    onChange={(e) => setSearch(e.currentTarget.value)}
                  />
                </TextField.Root>
              </Box>
              <Box>
                <Text size="1" color="gray" mb="1" as="div">
                  Started after
                </Text>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{
                    padding: 6,
                    borderRadius: 4,
                    border: '1px solid var(--gray-7)',
                    fontSize: 14,
                  }}
                />
              </Box>
              <Box>
                <Text size="1" color="gray" mb="1" as="div">
                  Started before
                </Text>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{
                    padding: 6,
                    borderRadius: 4,
                    border: '1px solid var(--gray-7)',
                    fontSize: 14,
                  }}
                />
              </Box>
              <Box style={{ minWidth: 200 }}>
                <Text size="1" color="gray" mb="1" as="div">
                  Status
                </Text>
                <Select.Root value={statusFilter} onValueChange={setStatusFilter}>
                  <Select.Trigger />
                  <Select.Content>
                    {STATUS_OPTIONS.map((s) => (
                      <Select.Item key={s.value} value={s.value}>
                        {s.label}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </Box>
              <Flex gap="2" ml="auto">
                <Button variant="soft" onClick={loadRuns} disabled={loading}>
                  <ReloadIcon /> Refresh
                </Button>
              </Flex>
            </Flex>

            {error && (
              <Box style={{ padding: 12, background: 'var(--red-3)', borderRadius: 4 }}>
                <Text color="red" size="2">
                  {error}
                </Text>
              </Box>
            )}

            {sessionLoaded && (!sessionOrgId || sessionOrgId === 'ALL') && (
              <Box style={{ padding: 12, background: 'var(--amber-3)', borderRadius: 4 }}>
                <Text size="2">Select an organization above to view EHR sync history.</Text>
              </Box>
            )}

            <Table.Root variant="surface">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>Patient</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Started</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Duration</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Changes</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Applicable / non</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Triggered by</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Acked</Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {loading && records.length === 0 && (
                  <Table.Row>
                    <Table.Cell colSpan={8}>
                      <Text color="gray" size="2">
                        Loading…
                      </Text>
                    </Table.Cell>
                  </Table.Row>
                )}
                {!loading && records.length === 0 && sessionOrgId && sessionOrgId !== 'ALL' && (
                  <Table.Row>
                    <Table.Cell colSpan={8}>
                      <Text color="gray" size="2">
                        No EHR sync runs in this date range.
                      </Text>
                    </Table.Cell>
                  </Table.Row>
                )}
                {records.map((r) => {
                  const badge = STATUS_BADGE_COLOR[r.status] || 'gray';
                  const apc = r.applicability_summary?.applicable_count ?? 0;
                  const nonApc = r.applicability_summary?.non_applicable_count ?? 0;
                  return (
                    <Table.Row
                      key={r.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setDetailRow(r)}
                    >
                      <Table.Cell>
                        <Flex direction="column" gap="1">
                          <Code variant="ghost">{r.patient_id}</Code>
                          <Text size="1" color="gray">
                            {formatName(r)}
                          </Text>
                        </Flex>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="1" color="gray">
                          {r.started_at ? new Date(r.started_at).toLocaleString() : '—'}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="1" color="gray">
                          {formatDuration(r.started_at, r.completed_at)}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge color={badge} size="1">
                          {r.status}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2">{bucketSummary(r.change_summary)}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2">
                          {apc} / {nonApc}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="1" color="gray">
                          {r.triggered_by || '—'}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        {r.acknowledged_at ? (
                          <Badge color="green" size="1">
                            ✓ {r.acknowledged_by || ''}
                          </Badge>
                        ) : (
                          <Text size="1" color="gray">
                            —
                          </Text>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table.Root>

            <Flex gap="2" justify="end" align="center">
              <Text size="2" color="gray">
                {total === 0 ? 'No results' : `Page ${page} of ${totalPages} · ${total} total`}
              </Text>
              <Button
                variant="soft"
                size="1"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Button
                variant="soft"
                size="1"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </Flex>
          </Flex>
        </Card>
      </Flex>

      {/* Per-org confirmation dialog. Wraps potentially-expensive action. */}
      <Dialog.Root open={orgConfirmOpen} onOpenChange={setOrgConfirmOpen}>
        <Dialog.Content style={{ maxWidth: 480 }}>
          <Dialog.Title>Run EHR sync for full org?</Dialog.Title>
          <Dialog.Description size="2" mb="3">
            This will start a background job that re-imports EHR FHIR data
            for every <Code variant="ghost">source=&apos;FHIR&apos;</Code> patient in this
            organization, capped by <Code variant="ghost">EHR_REFRESH_MAX_PATIENTS</Code>{' '}
            (default 50). LLM regen runs unless{' '}
            <Code variant="ghost">EHR_REFRESH_CARE_PLAN_ENABLED=false</Code> is set in
            the deployment env.
          </Dialog.Description>
          <Flex gap="2" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray" disabled={orgTriggerBusy}>
                Cancel
              </Button>
            </Dialog.Close>
            <Button color="amber" onClick={handleTriggerOrg} disabled={orgTriggerBusy}>
              {orgTriggerBusy ? 'Starting…' : 'Run for full org'}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* Per-row detail dialog. */}
      <Dialog.Root open={!!detailRow} onOpenChange={(open) => !open && setDetailRow(null)}>
        <Dialog.Content style={{ maxWidth: 720 }}>
          <Dialog.Title>EHR sync run #{detailRow?.id}</Dialog.Title>
          {detailRow && (
            <Flex direction="column" gap="3">
              <Flex gap="3" wrap="wrap">
                <Box>
                  <Text size="1" color="gray" as="div">
                    Patient
                  </Text>
                  <Text size="2">
                    <Code variant="ghost">{detailRow.patient_id}</Code> · {formatName(detailRow)}
                  </Text>
                </Box>
                <Box>
                  <Text size="1" color="gray" as="div">
                    Status
                  </Text>
                  <Badge color={STATUS_BADGE_COLOR[detailRow.status] || 'gray'} size="1">
                    {detailRow.status}
                  </Badge>
                </Box>
                <Box>
                  <Text size="1" color="gray" as="div">
                    Triggered by
                  </Text>
                  <Text size="2">{detailRow.triggered_by || '—'}</Text>
                </Box>
              </Flex>
              <Flex gap="3" wrap="wrap">
                <Box>
                  <Text size="1" color="gray" as="div">
                    Started
                  </Text>
                  <Text size="2">
                    {detailRow.started_at ? new Date(detailRow.started_at).toLocaleString() : '—'}
                  </Text>
                </Box>
                <Box>
                  <Text size="1" color="gray" as="div">
                    Completed
                  </Text>
                  <Text size="2">
                    {detailRow.completed_at
                      ? new Date(detailRow.completed_at).toLocaleString()
                      : '—'}
                  </Text>
                </Box>
                <Box>
                  <Text size="1" color="gray" as="div">
                    Duration
                  </Text>
                  <Text size="2">{formatDuration(detailRow.started_at, detailRow.completed_at)}</Text>
                </Box>
              </Flex>

              <Box>
                <Text size="1" color="gray" weight="bold" as="div">
                  Applicability summary
                </Text>
                <Text size="2">
                  Applicable: {detailRow.applicability_summary?.applicable_count ?? 0} · Non-applicable:{' '}
                  {detailRow.applicability_summary?.non_applicable_count ?? 0}
                </Text>
              </Box>

              <Box>
                <Text size="1" color="gray" weight="bold" as="div">
                  Change summary
                </Text>
                <pre
                  style={{
                    margin: 0,
                    fontSize: 12,
                    background: 'var(--gray-3)',
                    padding: 8,
                    borderRadius: 4,
                    overflow: 'auto',
                    maxHeight: 300,
                  }}
                >
                  {detailRow.change_summary
                    ? JSON.stringify(detailRow.change_summary, null, 2)
                    : '(none)'}
                </pre>
              </Box>

              {detailRow.error_detail && (
                <Box>
                  <Text size="1" color="red" weight="bold" as="div">
                    Error detail
                  </Text>
                  <pre
                    style={{
                      margin: 0,
                      fontSize: 12,
                      background: 'var(--red-3)',
                      padding: 8,
                      borderRadius: 4,
                      overflow: 'auto',
                      maxHeight: 220,
                    }}
                  >
                    {detailRow.error_detail}
                  </pre>
                </Box>
              )}

              {detailRow.acknowledged_at && (
                <Text size="2" color="gray">
                  Acknowledged at {new Date(detailRow.acknowledged_at).toLocaleString()} by{' '}
                  {detailRow.acknowledged_by || 'unknown'}
                </Text>
              )}

              <Flex gap="2" justify="end">
                {detailRow.draft_care_plan_id && detailRow.status === 'changes_found' && (
                  <Button
                    variant="soft"
                    onClick={() => {
                      navigate(`/?openPatientId=${encodeURIComponent(detailRow.patient_id)}`);
                    }}
                  >
                    View Care Plan Draft <ExternalLinkIcon />
                  </Button>
                )}
                <Dialog.Close>
                  <Button variant="soft" color="gray">
                    Close
                  </Button>
                </Dialog.Close>
              </Flex>
            </Flex>
          )}
        </Dialog.Content>
      </Dialog.Root>
    </AdminLayout>
  );
};

export default AdminEhrSyncPage;
